import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { CrossIcon } from '../../../../../assets/icons';
import UseContext from '../../../../../hooks/useContext';
import ApiDatabase from '../../../../../server';
import Button from '../../../../button';
import StyledInput from '../../../../styledInput/StyledInput';
import ModalBody from '../../../components/body';
import ModalFooter from '../../../components/footer';
import JustificatifConfirmation, {
  CONFIRMATION_TITLE,
} from 'components/justificatifConfirmation/JustificatifConfirmation';
import useJustificatifConfirmation from 'hooks/useJustificatifConfirmation';
import useBaseline from 'hooks/useBaseline';
import Modalheader from '../../../components/header';
import useModal from '../../../useModal';

const ModalNightHoursCompany = () => {
  const { toggle } = useModal();
  const { modalData, setModalDataReturn } = UseContext();
  const [isLoading, setIsLoading] = useState(false);
  const justificatif = useJustificatifConfirmation();
  const baseline = useBaseline([]);
  const [isWaiting, setIsWaiting] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [thresholds, setThresholds] = useState([
    {
      start: '',
      end: '',
      percentage: '',
    },
  ]);
  const [errors, setErrors] = useState([
    {
      start: null,
      end: null,
      percentage: null,
    },
  ]);
  const token = localStorage.getItem('xsrfToken');

  const addThreshold = () => {
    setThresholds((prevThresholds) => [
      ...prevThresholds,
      { start: '', end: '', percentage: '' },
    ]);
    setErrors((prevErrors) => [
      ...prevErrors,
      { start: null, end: null, percentage: null },
    ]);
  };

  const removeThreshold = (index) => {
    setThresholds((prevThresholds) =>
      prevThresholds.length > 1
        ? prevThresholds.filter((_, i) => i !== index)
        : prevThresholds
    );
    setErrors((prevErrors) =>
      prevErrors.length > 1
        ? prevErrors.filter((_, i) => i !== index)
        : prevErrors
    );
  };

  // Shared with handleChange so the inline error clears under the exact same rule
  // that gates submission.
  const validatePercentage = (value) => {
    const parsed = parseFloat(String(value).replace(',', '.'));
    if (!Number.isFinite(parsed)) return 'Saisissez un nombre.';
    if (parsed < 0 || parsed > 100) return 'La majoration doit être comprise entre 0 et 100.';
    if (Math.round(parsed * 10) / 10 !== parsed) return 'Au plus une décimale.';
    return null;
  };

  const validateThresholds = () => {
    const newErrors = thresholds.map((threshold) => {
      const errors = {
        start:
          !threshold.start || threshold.start > 24
            ? 'La valeur doit être entre 0 et 24'
            : null,
        end:
          !threshold.end || threshold.end > 24
            ? 'La valeur doit être entre 0 et 24'
            : null,
        percentage: validatePercentage(threshold.percentage),
      };

      if (
        threshold.start === threshold.end &&
        threshold.start &&
        threshold.end
      ) {
        errors.start = errors.end =
          'Les valeurs ne doivent pas être identiques';
      }

      return errors;
    });

    const intervals = [];
    for (let index = 0; index < thresholds.length; index++) {
      if (!newErrors[index].start && !newErrors[index].end) {
        const start = Number(thresholds[index].start);
        let end = Number(thresholds[index].end);

        if (start > end) {
          end += 24;
        }

        intervals.push({ start, end, index });
      }
    }

    intervals.sort((a, b) => a.start - b.start);

    for (let i = 0; i < intervals.length - 1; i++) {
      const current = intervals[i];
      const next = intervals[i + 1];

      if (next.start < current.end) {
        newErrors[current.index].start = 'Les périodes se chevauchent';
        newErrors[current.index].end = 'Les périodes se chevauchent';
        newErrors[next.index].start = 'Les périodes se chevauchent';
        newErrors[next.index].end = 'Les périodes se chevauchent';
      }
    }

    setErrors(newErrors);

    return newErrors.every(
      (error) => !error.start && !error.end && !error.percentage
    );
  };

  const handleSubmit = () => {
    if (!validateThresholds() || !modalData?.idCompany) return;

    // These values reach contracts and invoices, so the change carries the
    // client's written request for it before anything is written.
    // Nothing to confirm when nothing moved: the confirmation asks for a
    // justificatif, and there would be no change for it to justify.
    if (!baseline.hasChanged(thresholds)) return;
    if (!justificatif.isConfirming) return justificatif.setIsConfirming(true);
    justificatif.confirm(save);
  };

  const save = (storedJustificatif) => {
    setIsLoading(true);
      // The API requires percentage as an actual number (validateIncrease
      // rejects strings), start/end stay untouched.
      const nightHours = thresholds.map((threshold) => ({
        start: threshold.start,
        end: threshold.end,
        percentage: parseFloat(String(threshold.percentage).replace(',', '.')),
      }));
      ApiDatabase.postNightHoursCompany(
        {
          token,
          idCompany: modalData?.idCompany,
          nightHours,
          justificatif: storedJustificatif,
        },
        (data) => {
          setIsLoading(false);
          toast.success('Les heures de nuit ont étés mises à jour');
          setModalDataReturn({
            type: 'night_hours_updated',
            nightHours: data.nightHours,
          });
          toggle();
        },
        (err) => {
          setIsLoading(false);
          if (err.name === 'CanceledError') return;
          // The modal stays open, the input intact.
          setApiError(err?.response?.data?.message ?? "L'enregistrement a échoué");
        }
      );
  };

  const handleChange = (index, field, value) => {
    setThresholds((prevThresholds) =>
      prevThresholds.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    );

    setErrors((prevErrors) =>
      prevErrors.map((error, i) => {
        if (i !== index) return error;

        const updatedError = { ...error };
        if (field === 'start' || field === 'end') {
          updatedError[field] =
            value > 24 ? 'La valeur doit être entre 0 et 24' : null;
        }
        if (field === 'percentage') {
          updatedError.percentage = validatePercentage(value);
        }
        return updatedError;
      })
    );
    setApiError(null);
  };

  useEffect(() => {
    if (!modalData?.idCompany) return;

    setIsWaiting(true);
    ApiDatabase.getNightHoursCompany(
      { token, idCompany: modalData.idCompany },
      (data) => {
        const provThreshold = data.nightHours?.length
          ? data.nightHours.map(({ start, end, percentage }) => ({
              start,
              end,
              percentage: percentage === undefined || percentage === null ? '' : String(percentage),
            }))
          : [
              {
                start: '',
                end: '',
                percentage: '',
              },
            ];
        setThresholds(provThreshold);
        baseline.capture(provThreshold);
        setIsWaiting(false);
      },
      (err) => {
        if (err.name !== 'CanceledError') {
          console.error(err);
          setIsWaiting(false);
        }
      }
    );
  }, [modalData?.idCompany]);

  return (
    <>
      <Modalheader hide={toggle} hideShow={true}>
        {justificatif.isConfirming ? CONFIRMATION_TITLE : 'Heures de nuit'}
      </Modalheader>
      <ModalBody>
        {justificatif.isConfirming ? (
          <JustificatifConfirmation
            file={justificatif.file}
            setFile={justificatif.setFile}
            isAttested={justificatif.isAttested}
            setIsAttested={justificatif.setIsAttested}
            error={justificatif.error}
          />
        ) : (
          <>
        <div className="flex flex-col gap-2.5 text-sm">
          <div className={'text-gray-500'}>
            Les majorations que vous définissez ici s’appliquent aux heures
            travaillées sur les plages horaires définies.
          </div>
          {isWaiting ? (
            <div>
              {[1, 2, 3].map((i, index) => (
                <div key={index} className="rounded-lg bg-gray-50 p-4">
                  <div className="flex flex-col gap-3">
                    <div className={'flex gap-1 justify-between'}>
                      <div className="w-56 h-4 skeleton-bar"></div>
                      <div className="w-8 h-8 skeleton-bar"></div>
                    </div>
                    <div className="flex gap-3 w-full">
                      <div className="w-full h-14 skeleton-bar"></div>
                      <div className="w-full h-14 skeleton-bar"></div>
                    </div>
                    <div>
                      <div className="w-full h-14 skeleton-bar"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <>
              {thresholds.map((threshold, index) => (
                <div key={index} className="rounded-lg bg-gray-50 p-4">
                  <div className="flex flex-col gap-3">
                    <div className={'flex gap-1 justify-between'}>
                      <div>PÉRIODE {index + 1}</div>
                      {thresholds.length > 1 && (
                        <div>
                          <Button
                            size={'ICON'}
                            color={'ICON'}
                            onClick={() => removeThreshold(index)}
                          >
                            <CrossIcon wh={20} color={'#374151'} />
                          </Button>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-3 w-full">
                      <div className={'w-full'}>
                        <StyledInput
                          type="number"
                          label="À partir de"
                          value={threshold.start}
                          unit={'H'}
                          onChange={(e) =>
                            handleChange(index, 'start', e.target.value)
                          }
                          error={errors[index]?.start}
                        />
                      </div>
                      <div className={'w-full'}>
                        <StyledInput
                          type="number"
                          label="Jusqu'à"
                          value={threshold.end}
                          unit={'H'}
                          onChange={(e) =>
                            handleChange(index, 'end', e.target.value)
                          }
                          error={errors[index]?.end}
                        />
                      </div>
                    </div>
                    <div>
                      <label className={'flex flex-col gap-1'}>
                        Majoration
                        <div className={'flex items-center gap-2'}>
                          <input
                            type={'text'}
                            className={`border rounded px-2 py-2 w-full ${
                              errors[index]?.percentage ? 'border-red-500' : ''
                            }`}
                            value={threshold.percentage}
                            onChange={(e) => handleChange(index, 'percentage', e.target.value)}
                          />
                          <span className={'text-gray-500'}>%</span>
                        </div>
                      </label>
                      {errors[index]?.percentage && (
                        <div className={'text-red-500 text-xs'}>{errors[index].percentage}</div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
          {apiError && <div className={'text-red-500 text-xs'}>{apiError}</div>}
        </div>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <div className={'w-full flex gap-1 justify-between items-center'}>
          <div>
            {!justificatif.isConfirming && (
              <Button size={'LG'} color={'SECONDARY'} onClick={addThreshold}>
                Ajouter un seuil
              </Button>
            )}
          </div>
          <div className={'flex gap-3'}>
            {isLoading || justificatif.isUploading ? (
              <>
                <Button size={'LG'} color={'SECONDARY'}>
                  Annuler
                </Button>
                <Button size={'LG'} color={'DISABLED'}>
                  Confirmation ...
                </Button>
              </>
            ) : (
              <>
                <Button
                  size={'LG'}
                  color={'SECONDARY'}
                  // Backs out of the confirmation, not the modal: the values
                  // being confirmed are still worth seeing.
                  onClick={
                    justificatif.isConfirming
                      ? () => justificatif.setIsConfirming(false)
                      : toggle
                  }
                >
                  Annuler
                </Button>
                <Button
                  size={'LG'}
                  color={
                    !baseline.hasChanged(thresholds) ||
              (justificatif.isConfirming && !justificatif.canConfirm)
                      ? 'DISABLED'
                      : 'PRIMARY'
                  }
                  onClick={handleSubmit}
                >
                  Confirmer
                </Button>
              </>
            )}
          </div>
        </div>
      </ModalFooter>
    </>
  );
};

export default ModalNightHoursCompany;
